import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { DocumentNode, CpSelectorNode, CommissioningPackage, ProcedureDocument } from '@completion/models';
import { Store } from '@ngrx/store';
import { getCurrentCpId, getCurrentDocument, selectEditMode } from '@completion/selectors';
import { Subject } from 'rxjs';
import { takeUntil, take } from 'rxjs/operators';
import { DocumentUpdateQueueService } from '../../../../core/services/document-update-queue.service';
import { MatDialog } from '@angular/material/dialog';
import { CpSelectDialogComponent, CpSelectDialogData } from '../../../../shared/cp-select/cp-select-dialog.component';
import { persistNodeUpdateForNode } from '../node-persistence.util';

@Component({
  selector: 'app-cp-selector',
  templateUrl: './cp-selector.component.html',
  styleUrls: ['./cp-selector.component.scss'],
  standalone: false,
})
export class CpSelectorComponent implements OnInit, OnDestroy {
  documentNode: CpSelectorNode; // reference only (do not mutate)
  selectedCp: CommissioningPackage | undefined;
  currentCpId: number | null = null;
  editMode = false; // now populated from store
  private readonly destroy$ = new Subject<void>();

  constructor(
    @Inject('documentNode') documentNode: DocumentNode,
    private store: Store,
    private updateQueue: DocumentUpdateQueueService,
    private dialog: MatDialog,
  ) {
    // Keep original reference for id lookup, but never mutate its properties directly.
    this.documentNode = documentNode as CpSelectorNode;
  }

  ngOnInit(): void {
    // Initial local selection state from injected node
    this.selectedCp = this.documentNode?.commissioningPackage;

    // Track current CP id
    this.store
      .select(getCurrentCpId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((id) => (this.currentCpId = id ?? null));
    // Track edit mode
    this.store
      .select(selectEditMode)
      .pipe(takeUntil(this.destroy$))
      .subscribe((m) => (this.editMode = m));

    // Keep local selectedCp in sync with store document updates (e.g. remote changes)
    this.store
      .select(getCurrentDocument)
      .pipe(takeUntil(this.destroy$))
      .subscribe((doc: ProcedureDocument | null) => {
        if (!doc) {
          return;
        }
        const found = this.findNodeById(doc.nodes || [], (this.documentNode as any).id);
        if (found) {
          const newCp = (found as CpSelectorNode).commissioningPackage;
          // Update local selection if changed (compare ids to avoid unnecessary re-renders)
          if ((this.selectedCp?.id ?? null) !== (newCp?.id ?? null)) {
            this.selectedCp = newCp;
          }
        }
      });
  }

  private findNodeById(nodes: DocumentNode[] = [], id: number): DocumentNode | undefined {
    for (const n of nodes) {
      if (n.id === id) return n;
      if (n.children?.length) {
        const childHit = this.findNodeById(n.children, id);
        if (childHit) return childHit;
      }
    }
    return undefined;
  }

  get isCurrentCpSelected(): boolean {
    const selectedId = this.selectedCp?.id ?? null;
    return selectedId != null && this.currentCpId != null && selectedId === this.currentCpId;
  }

  editCp() {
    const preselectedId = this.selectedCp?.id;
    const data: CpSelectDialogData = { preselectedCpId: preselectedId, title: 'Select CP' };
    const ref = this.dialog.open(CpSelectDialogComponent, { width: '500px', data });
    ref
      .afterClosed()
      .pipe(take(1))
      .subscribe((cp: CommissioningPackage | undefined) => {
        if (!cp) {
          return;
        }
        // Optimistically update local view state only (do NOT mutate documentNode which may be frozen)
        this.selectedCp = cp;
        // Persist change immutably using shared helper
        persistNodeUpdateForNode(this.store, this.updateQueue, this.documentNode, (n: any) => {
          n.commissioningPackage = cp;
        });
      });
  }

  removeCp() {
    if (!this.selectedCp) {
      return;
    }
    if (this.isCurrentCpSelected) {
      return;
    }

    // Optimistically clear local selection; do NOT mutate injected node
    this.selectedCp = undefined;

    // Persist via immutable document update
    persistNodeUpdateForNode(this.store, this.updateQueue, this.documentNode, (n: any) => {
      n.commissioningPackage = undefined;
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
